// JigsawPuzzle.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './JigsawPuzzle.css';

const JigsawPuzzle = ({ 
  image, 
  onComplete, 
  difficulty = "expert", 
  onClose,
  aspectRatio = "2:3" // Default vertical rectangle (2:3 aspect ratio)
}) => {
  const containerRef = useRef(null);
  const [grid, setGrid] = useState([]);
  const [isSorted, setIsSorted] = useState(false);
  const [fullImageLoaded, setFullImageLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Calculate aspect ratio from string (e.g., "2:3")
  const calculateAspectRatio = (ratioStr) => {
    const [width, height] = ratioStr.split(':').map(Number);
    return { width, height };
  };

  const { width: aspectWidth, height: aspectHeight } = calculateAspectRatio(aspectRatio);
  const aspectRatioValue = aspectWidth / aspectHeight;

  // Update container size on resize
  const updateContainerSize = useCallback(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.parentElement.offsetWidth;
      const calculatedHeight = containerWidth / aspectRatioValue;
      setContainerSize({ width: containerWidth, height: calculatedHeight });
    }
  }, [aspectRatioValue]);

  // Initialize puzzle
  const initPuzzle = useCallback(() => {
    if (!image) return;

    const img = new Image();
    img.onload = () => {
      const numColumns = 3;
      const numRows = 4; // 4 rows for vertical rectangle (3x4 = 12 pieces)
      const gridSize = numColumns * numRows;
      
      // Build grid array with correct positions
      const gridArr = [];
      let currentRow = 0;
      let currentColumn = 1;
      
      for (let i = 0; i < gridSize; i++) {
        const position = i + 1;
        
        // Calculate image positioning for 3x4 grid
        let top = 0;
        let left = 0;
        
        // Set top alignment (4 rows)
        const rowsPerSection = Math.floor(gridSize / numRows);
        if (position % rowsPerSection === 0) {
          top = -100 * currentRow;
          currentRow++;
        } else {
          top = -100 * currentRow;
        }
        
        // Set left alignment (3 columns)
        if (i % numColumns !== 0) {
          left = -100 * currentColumn;
          currentColumn++;
        } else {
          currentColumn = 1;
        }
        
        gridArr.push({
          id: i,
          position: position,
          originalPosition: position,
          top: top,
          left: left,
          inPlace: false,
          isGray: true
        });
      }
      
      // Shuffle based on difficulty - ensure NO tiles are in correct position
      const shuffledGrid = shuffleArrayWithNoCorrectPositions(gridArr, difficulty);
      setGrid(shuffledGrid);
      setIsLoading(false);
      
      // Update container size after image loads
      setTimeout(updateContainerSize, 100);
    };
    
    img.src = image;
  }, [image, difficulty, updateContainerSize]);

  // Fisher-Yates shuffle with guarantee that no element stays in original position
  const shuffleArrayWithNoCorrectPositions = (array, diff) => {
    const shuffled = [...array];
    const n = shuffled.length;
    
    // First, do a normal Fisher-Yates shuffle
    for (let i = n - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    // Check if any element is in its original position
    // If yes, swap with another random element
    let hasCorrectPosition;
    do {
      hasCorrectPosition = false;
      
      for (let i = 0; i < n; i++) {
        if (shuffled[i].originalPosition === (i + 1)) {
          hasCorrectPosition = true;
          
          // Find a random position to swap with (excluding current position)
          let swapIndex;
          do {
            swapIndex = Math.floor(Math.random() * n);
          } while (swapIndex === i || 
                  shuffled[swapIndex].originalPosition === (i + 1) ||
                  shuffled[i].originalPosition === (swapIndex + 1));
          
          // Swap elements
          [shuffled[i], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[i]];
        }
      }
    } while (hasCorrectPosition);
    
    // Apply difficulty - for easier difficulties, keep some pieces in correct position
    // But for your requirement, we'll ensure NO pieces are correct at start
    // So difficulty only affects how shuffled it is
    const difficultyMap = {
      "easy": 0.7,    // Less shuffled
      "normal": 0.5,  // Moderately shuffled
      "hard": 0.3,    // Well shuffled
      "expert": 0     // Completely random
    };
    
    const difficultyFactor = difficultyMap[diff] || 0;
    
    // Additional shuffling based on difficulty
    const additionalShuffles = Math.floor(n * difficultyFactor);
    for (let s = 0; s < additionalShuffles; s++) {
      const i = Math.floor(Math.random() * n);
      const j = Math.floor(Math.random() * n);
      if (i !== j) {
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
    }
    
    // Final check to ensure no pieces are in correct position
    shuffled.forEach((tile, index) => {
      tile.inPlace = false;
      tile.isGray = true;
    });
    
    return shuffled;
  };

  // Alternative shuffle function that guarantees derangement (no fixed points)
  const derangementShuffle = (array, diff) => {
    const n = array.length;
    const shuffled = [...array];
    
    // Create a derangement - permutation with no fixed points
    let derangement = Array.from({ length: n }, (_, i) => i);
    
    // Simple algorithm to create a derangement
    for (let i = n - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * i);
      [derangement[i], derangement[j]] = [derangement[j], derangement[i]];
    }
    
    // Check for fixed points and fix them
    for (let i = 0; i < n; i++) {
      if (derangement[i] === i) {
        // Swap with a random other position
        let swapWith;
        do {
          swapWith = Math.floor(Math.random() * n);
        } while (swapWith === i);
        
        [derangement[i], derangement[swapWith]] = [derangement[swapWith], derangement[i]];
      }
    }
    
    // Apply the derangement to our array
    const result = new Array(n);
    for (let i = 0; i < n; i++) {
      result[i] = shuffled[derangement[i]];
    }
    
    // Apply difficulty factor by optionally swapping more
    const difficultyMap = {
      "easy": 2,
      "normal": 4,
      "hard": 8,
      "expert": 12
    };
    
    const extraSwaps = difficultyMap[diff] || 0;
    for (let s = 0; s < extraSwaps; s++) {
      const i = Math.floor(Math.random() * n);
      const j = Math.floor(Math.random() * n);
      if (i !== j) {
        [result[i], result[j]] = [result[j], result[i]];
      }
    }
    
    // Set initial states
    result.forEach((tile, index) => {
      tile.inPlace = false;
      tile.isGray = true;
    });
    
    return result;
  };

  // Handle drag start
  const handleDragStart = (e, index) => {
    if (isSorted || grid[index].inPlace) return;
    
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
    
    e.target.classList.add('highlight');
  };

  // Handle drag over
  const handleDragOver = (e) => {
    if (isSorted) return;
    
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (!e.target.draggable) {
      e.target.classList.add('highlight');
    }
  };

  // Handle drag leave
  const handleDragLeave = (e) => {
    if (!e.target.draggable) {
      e.target.classList.remove('highlight');
    }
  };

  // Handle drop
  const handleDrop = (e, dropIndex) => {
    if (isSorted) return;
    
    e.preventDefault();
    e.target.classList.remove('highlight');
    
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (dragIndex === dropIndex || grid[dropIndex].inPlace) return;
    
    // Swap tiles
    const newGrid = [...grid];
    [newGrid[dragIndex], newGrid[dropIndex]] = [newGrid[dropIndex], newGrid[dragIndex]];
    
    // Check if tiles are in correct position
    newGrid.forEach((tile, index) => {
      const isCorrectPosition = tile.originalPosition === (index + 1);
      tile.inPlace = isCorrectPosition;
      tile.isGray = !isCorrectPosition;
    });
    
    setGrid(newGrid);
    
    // Check if puzzle is complete
    const completed = newGrid.every((tile, index) => tile.originalPosition === (index + 1));
    if (completed) {
      setIsSorted(true);
      
      setTimeout(() => {
        setFullImageLoaded(true);
      }, 500);
      
      if (onComplete) {
        onComplete();
      }
    }
  };

  // Handle touch events for mobile
  const [touchStart, setTouchStart] = useState(null);
  const [draggedTile, setDraggedTile] = useState(null);

  const handleTouchStart = (e, index) => {
    if (isSorted || grid[index].inPlace) return;
    
    setTouchStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      index
    });
    setDraggedTile(index);
    e.target.classList.add('highlight');
  };

  const handleTouchMove = (e) => {
    if (isSorted) return;
    
    if (draggedTile !== null && containerRef.current) {
      const touch = e.touches[0];
      const tileElement = containerRef.current.children[draggedTile];
      
      if (tileElement) {
        tileElement.style.transform = `translate(${touch.clientX - touchStart.x}px, ${touch.clientY - touchStart.y}px)`;
        tileElement.style.zIndex = '10';
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (isSorted) return;
    
    if (draggedTile !== null && touchStart && containerRef.current) {
      const touch = e.changedTouches[0];
      
      // Find drop target
      const dropTarget = Array.from(containerRef.current.children).findIndex((child, index) => {
        if (grid[index]?.inPlace) return false;
        
        const rect = child.getBoundingClientRect();
        return touch.clientX >= rect.left && 
               touch.clientX <= rect.right && 
               touch.clientY >= rect.top && 
               touch.clientY <= rect.bottom;
      });
      
      if (dropTarget !== -1 && dropTarget !== draggedTile && !grid[dropTarget]?.inPlace) {
        // Swap tiles
        const newGrid = [...grid];
        [newGrid[draggedTile], newGrid[dropTarget]] = [newGrid[dropTarget], newGrid[draggedTile]];
        
        // Check if tiles are in correct position
        newGrid.forEach((tile, index) => {
          const isCorrectPosition = tile.originalPosition === (index + 1);
          tile.inPlace = isCorrectPosition;
          tile.isGray = !isCorrectPosition;
        });
        
        setGrid(newGrid);
        
        // Check if puzzle is complete
        const completed = newGrid.every((tile, index) => tile.originalPosition === (index + 1));
        if (completed) {
          setIsSorted(true);
          
          setTimeout(() => {
            setFullImageLoaded(true);
          }, 500);
          
          if (onComplete) {
            onComplete();
          }
        }
      }
      
      // Reset tile position
      const tileElement = containerRef.current.children[draggedTile];
      if (tileElement) {
        tileElement.style.transform = '';
        tileElement.style.zIndex = '';
        tileElement.classList.remove('highlight');
      }
      
      setDraggedTile(null);
      setTouchStart(null);
    }
  };

  // Handle close button click
  const handleCloseClick = () => {
    if (onClose) {
      onClose();
    }
  };

  // Handle window resize
  useEffect(() => {
    updateContainerSize();
    window.addEventListener('resize', updateContainerSize);
    
    return () => {
      window.removeEventListener('resize', updateContainerSize);
    };
  }, [updateContainerSize]);

  useEffect(() => {
    initPuzzle();
  }, [initPuzzle]);

  // Adjust for mobile orientation
  useEffect(() => {
    const handleOrientationChange = () => {
      setTimeout(updateContainerSize, 300); // Delay to allow orientation to settle
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [updateContainerSize]);

  if (isLoading) {
    return <div className="puzzle-loading">Loading puzzle...</div>;
  }

  const numColumns = 3;
  const numRows = 4;

  return (
    <div className="jigsaw-puzzle-container">
      <div className="puzzle-header">
        <h3 className="puzzle-title">Drag and drop the puzzle pieces to complete the jigsaw and identify the door where the currency plates are hidden.</h3>
      </div>
      
      <div 
        ref={containerRef}
        className={`puzzle-frame ${isSorted ? 'puzzle-solved' : ''}`}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          height: containerSize.height > 0 ? `${containerSize.height}px` : '500px',
          maxHeight: '70vh' // Limit height for mobile
        }}
      >
        {grid.map((tile, index) => (
          <div
            key={tile.id}
            className={`puzzle-slot ${tile.inPlace ? 'in-place locked' : ''}`}
            draggable={!tile.inPlace && !isSorted}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onTouchStart={(e) => handleTouchStart(e, index)}
            style={{
              flex: `1 0 ${100 / numColumns}%`,
              maxWidth: `${100 / numColumns}%`,
              height: `${100 / numRows}%`,
              pointerEvents: (tile.inPlace || isSorted) ? 'none' : 'auto',
              cursor: (tile.inPlace || isSorted) ? 'default' : 'grab'
            }}
          >
            <div 
              className={`puzzle-piece ${tile.isGray ? 'gray-piece' : ''}`}
              data-position={tile.position}
              data-inplace={tile.inPlace}
              style={{
                backgroundImage: `url(${image})`,
                backgroundPosition: `${tile.left}% ${tile.top}%`,
                backgroundSize: `${numColumns * 100}% ${numRows * 100}%`
              }}
            />
          </div>
        ))}
        
        {fullImageLoaded && (
          <img 
            src={image} 
            alt="Completed puzzle" 
            className="puzzle-full-image"
            style={{
              objectFit: 'contain'
            }}
          />
        )}
      </div>
      
      {isSorted && (
        <div className="puzzle-complete-section">
          <button 
            className="puzzle-close-button"
            onClick={handleCloseClick}
          >
            Close 
          </button>
        </div>
      )}
    </div>
  );
};

export default JigsawPuzzle;